import { c as _c } from "react/compiler-runtime";
import { cn } from '@/lib/utils';
import type { UploadHookControl } from '@better-upload/client';
import { Loader2, Upload } from 'lucide-react';
import { useId } from 'react';
import { useDropzone } from 'react-dropzone';
type UploadDropzoneProps = {
  control: UploadHookControl<true>;
  id?: string;
  accept?: string;
  metadata?: Record<string, unknown>;
  description?: {
    fileTypes?: string;
    maxFileSize?: string;
    maxFiles?: number;
  } | string;
  uploadOverride?: (...args: Parameters<UploadHookControl<true>['upload']>) => void;

  // Add any additional props you need.
};
export function UploadDropzone(t0) {
  const $ = _c(35);
  const {
    control: t1,
    id: _id,
    accept,
    metadata,
    description,
    uploadOverride
  } = t0;
  const {
    upload,
    isPending
  } = t1;
  const id = useId();
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    inputRef: t2
  } = useDropzone({
    onDrop: files => {
      if (files.length > 0 && !isPending) {
        if (uploadOverride) {
          uploadOverride(files, {
            metadata
          });
        } else {
          upload(files, {
            metadata
          });
        }
      }
      inputRef.current.value = "";
    },
    noClick: true
  });
  const inputRef = t2;
  let t3;
  if ($[0] !== isDragActive) {
    t3 = cn("border-input text-foreground relative rounded-lg border border-dashed transition-colors", {
      "border-primary/80": isDragActive
    });
    $[0] = isDragActive;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== getRootProps) {
    t4 = getRootProps();
    $[2] = getRootProps;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = !isPending;
  let t6;
  if ($[4] !== isDragActive || $[5] !== isPending || $[6] !== t5) {
    t6 = cn("dark:bg-input/10 flex w-full min-w-72 cursor-pointer flex-col items-center justify-center rounded-lg bg-transparent px-2 py-6 transition-colors", {
      "text-muted-foreground cursor-not-allowed": isPending,
      "hover:bg-accent dark:hover:bg-accent/40": t5,
      "opacity-0": isDragActive
    });
    $[4] = isDragActive;
    $[5] = isPending;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = _id || id;
  let t8;
  if ($[8] !== isPending) {
    t8 = <div className="my-2">{isPending ? <Loader2 className="size-6 animate-spin" /> : <Upload className="size-6" />}</div>;
    $[8] = isPending;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="text-sm font-semibold">Drag and drop files here</p>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== description) {
    t10 = typeof description === "string" ? description : <>{description?.maxFiles && `You can upload ${description.maxFiles} file${description.maxFiles !== 1 ? "s" : ""}.`}{" "}{description?.maxFileSize && `${description.maxFiles !== 1 ? "Each u" : "U"}p to ${description.maxFileSize}.`}{" "}{description?.fileTypes && `Accepted ${description.fileTypes}.`}</>;
    $[11] = description;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10) {
    t11 = <div className="mt-3 space-y-1 text-center">{t9}<p className="text-muted-foreground max-w-64 text-xs">{t10}</p></div>;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== getInputProps) {
    t12 = getInputProps();
    $[15] = getInputProps;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const t13 = _id || id;
  let t14;
  if ($[17] !== accept || $[18] !== isPending || $[19] !== t12 || $[20] !== t13) {
    t14 = <input {...t12} type="file" multiple={true} id={t13} accept={accept} disabled={isPending} />;
    $[17] = accept;
    $[18] = isPending;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t11 || $[23] !== t14 || $[24] !== t4 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t15 = <label {...t4} className={t6} htmlFor={t7}>{t8}{t11}{t14}</label>;
    $[22] = t11;
    $[23] = t14;
    $[24] = t4;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== isDragActive) {
    t16 = isDragActive && <div className="pointer-events-none absolute inset-0 rounded-lg"><div className="dark:bg-accent/40 bg-accent flex size-full flex-col items-center justify-center rounded-lg"><div className="my-2"><Upload className="size-6" /></div><p className="mt-3 text-sm font-semibold">Drop files here</p></div></div>;
    $[29] = isDragActive;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t15 || $[32] !== t16 || $[33] !== t3) {
    t17 = <div className={t3}>{t15}{t16}</div>;
    $[31] = t15;
    $[32] = t16;
    $[33] = t3;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  return t17;
}